// src/pages/analytics/UserActivityAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, Chip, useTheme, alpha,
  Breadcrumbs, Link, Tab, Tabs, CircularProgress, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Person as UserIcon,
  Security as AdminIcon,
  Event as ActionIcon,
  Timer as TimeIcon,
  AccessTime as DurationIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  Preview as ViewsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as CreateIcon,
  VerifiedUser as LoginIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart, Scatter, ScatterChart, ZAxis
} from 'recharts';

// Import custom components
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportButton from '../../components/features/analytics/ExportButton';
import AnalyticsFilters from '../../components/features/analytics/AnalyticsFilters';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportAnalyticsData } from '../../services/analyticsService';

// Import utils
import { formatNumber } from '../../utils/analyticsUtils';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
} from '../../styles/analyticsResponsive';

/**
 * User Activity Analytics Page Component - Admin Only
 */
const UserActivityAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // Define filter state
  const [filters, setFilters] = useState({
    userId: 'all',
    actionType: 'all',
    module: 'all',
    userRole: 'all',
  });
  
  const { 
    userActivityAnalytics, isLoading, criticalError, fetchUserActivityAnalytics,
    dateRange, setDateRange, clearError,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Define filter options
  const filterOptions = [
    {
      key: 'actionType',
      label: 'Action Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All Actions' },
        { value: 'view', label: 'View' },
        { value: 'create', label: 'Create' },
        { value: 'update', label: 'Update' },
        { value: 'delete', label: 'Delete' },
        { value: 'login', label: 'Login' },
        { value: 'logout', label: 'Logout' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'module',
      label: 'Module',
      type: 'select',
      options: [
        { value: 'all', label: 'All Modules' },
        { value: 'clients', label: 'Clients' },
        { value: 'projects', label: 'Projects' },
        { value: 'tasks', label: 'Tasks' },
        { value: 'content', label: 'Content' },
        { value: 'campaigns', label: 'Campaigns' },
        { value: 'users', label: 'Users' },
        { value: 'settings', label: 'Settings' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'userRole',
      label: 'User Role',
      type: 'select',
      options: [
        { value: 'all', label: 'All Roles' },
        { value: 'admin', label: 'Admin' },
        { value: 'manager', label: 'Manager' },
        { value: 'creator', label: 'Creator' },
        { value: 'viewer', label: 'Viewer' },
      ],
      defaultValue: 'all',
    },
  ];
  
  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchUserActivityAnalytics({
          userId: filters.userId !== 'all' ? filters.userId : undefined,
          actionType: filters.actionType !== 'all' ? filters.actionType : undefined,
          module: filters.module !== 'all' ? filters.module : undefined,
          userRole: filters.userRole !== 'all' ? filters.userRole : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        });
      } catch (error) {
        showError(`Failed to load user activity analytics: ${error.message || 'Unknown error'}`);
      }
    };
    
    loadData();
  }, [fetchUserActivityAnalytics, showError, dateRange, filters]);
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Prepare export params
      const exportParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        // Include filter parameters
        userId: filters.userId !== 'all' ? filters.userId : undefined,
        actionType: filters.actionType !== 'all' ? filters.actionType : undefined,
        module: filters.module !== 'all' ? filters.module : undefined,
        userRole: filters.userRole !== 'all' ? filters.userRole : undefined,
        reportType: 'userActivity',
      };
      
      // Call the export service
      await exportAnalyticsData(exportParams);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Check if data is available
  const hasData = !isLoading && userActivityAnalytics;
  
  // Loading state
  if (isLoading && !userActivityAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !userActivityAnalytics) {
    return (
      <Box sx={{ py: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" href="/analytics">Analytics</Link>
          <Typography color="text.primary">User Activity</Typography>
        </Breadcrumbs>
        
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => {
                  clearError();
                  fetchUserActivityAnalytics();
                }}
              >
                Retry
              </Button>
            }
          >
            {criticalError}
          </Alert>
        </Box>
      </Box>
    );
  }
  
  // Mock data for development - replace with real data from API
  const userActivityTrends = [
    { date: '2023-01-01', totalActions: 450, uniqueUsers: 25 },
    { date: '2023-01-02', totalActions: 520, uniqueUsers: 28 },
    { date: '2023-01-03', totalActions: 480, uniqueUsers: 26 },
    { date: '2023-01-04', totalActions: 550, uniqueUsers: 30 },
    { date: '2023-01-05', totalActions: 620, uniqueUsers: 32 },
    { date: '2023-01-06', totalActions: 580, uniqueUsers: 29 },
    { date: '2023-01-07', totalActions: 490, uniqueUsers: 27 },
  ];
  
  const actionTypeData = [
    { name: 'View', value: 65 },
    { name: 'Create', value: 15 },
    { name: 'Update', value: 12 },
    { name: 'Delete', value: 3 },
    { name: 'Login/Logout', value: 5 },
  ];
  
  const moduleUsageData = [
    { module: 'Clients', views: 320, creates: 45, updates: 120, deletes: 5 },
    { module: 'Projects', views: 280, creates: 65, updates: 95, deletes: 8 },
    { module: 'Tasks', views: 420, creates: 180, updates: 310, deletes: 25 },
    { module: 'Content', views: 350, creates: 120, updates: 85, deletes: 12 },
    { module: 'Campaigns', views: 180, creates: 35, updates: 65, deletes: 4 },
    { module: 'Users', views: 150, creates: 12, updates: 28, deletes: 2 },
  ];
  
  const topActiveUsers = [
    { name: 'John Smith', role: 'Admin', actions: 520, avgDuration: 45 },
    { name: 'Sarah Johnson', role: 'Manager', actions: 480, avgDuration: 38 },
    { name: 'Mike Williams', role: 'Creator', actions: 350, avgDuration: 42 },
    { name: 'Emily Brown', role: 'Manager', actions: 320, avgDuration: 36 },
    { name: 'David Lee', role: 'Creator', actions: 280, avgDuration: 40 },
  ];
  
  const userActivityLog = [
    { id: 1, user: 'John Smith', action: 'Created', module: 'Project', item: 'Summer Campaign', timestamp: '2023-01-07 14:23:45' },
    { id: 2, user: 'Sarah Johnson', action: 'Updated', module: 'Task', item: 'Design Homepage', timestamp: '2023-01-07 13:45:12' },
    { id: 3, user: 'Mike Williams', action: 'Viewed', module: 'Client', item: 'Acme Corp', timestamp: '2023-01-07 12:32:08' },
    { id: 4, user: 'Emily Brown', action: 'Deleted', module: 'Content', item: 'Draft Post', timestamp: '2023-01-07 11:15:30' },
    { id: 5, user: 'David Lee', action: 'Updated', module: 'Campaign', item: 'Product Launch', timestamp: '2023-01-07 10:48:22' },
    { id: 6, user: 'John Smith', action: 'Viewed', module: 'Analytics', item: 'Dashboard', timestamp: '2023-01-07 09:22:15' },
    { id: 7, user: 'Sarah Johnson', action: 'Created', module: 'Task', item: 'Client Meeting', timestamp: '2023-01-07 08:55:40' },
    { id: 8, user: 'Mike Williams', action: 'Updated', module: 'Content', item: 'Blog Post', timestamp: '2023-01-06 17:40:12' },
    { id: 9, user: 'Emily Brown', action: 'Created', module: 'Project', item: 'Q1 Marketing', timestamp: '2023-01-06 16:25:08' },
    { id: 10, user: 'David Lee', action: 'Viewed', module: 'Analytics', item: 'Campaign Results', timestamp: '2023-01-06 15:10:45' },
  ];
  
  const userSessionData = [
    { hour: '8am', sessions: 12 },
    { hour: '9am', sessions: 18 },
    { hour: '10am', sessions: 25 },
    { hour: '11am', sessions: 30 },
    { hour: '12pm', sessions: 22 },
    { hour: '1pm', sessions: 18 },
    { hour: '2pm', sessions: 28 },
    { hour: '3pm', sessions: 32 },
    { hour: '4pm', sessions: 26 },
    { hour: '5pm', sessions: 20 },
    { hour: '6pm', sessions: 15 },
  ];
  
  const userRoleDistribution = [
    { name: 'Admin', value: 5 },
    { name: 'Manager', value: 12 },
    { name: 'Creator', value: 25 },
    { name: 'Viewer', value: 18 },
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">User Activity</Typography>
        </Breadcrumbs>
        
        <Box sx={getPageHeaderStyles(theme)}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            User Activity Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <AnalyticsFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              filterOptions={filterOptions}
              showDateRange={true}
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Error message */}
      {criticalError && (
        <Alert 
          severity="error" 
          onClose={() => clearError()}
          sx={{ mb: 4 }}
        >
          {criticalError}
        </Alert>
      )}
      
      {/* Overview KPIs */}
      <Grid container {...getKpiGridProps(theme).container}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Actions"
            value={3590}
            previousValue={3150}
            percentChange={14}
            icon={<ActionIcon />}
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Active Users"
            value={35}
            previousValue={32}
            percentChange={9.4}
            icon={<UserIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Avg. Session Time"
            value={"27:45"}
            unit=""
            previousValue={"24:30"}
            percentChange={13.3}
            icon={<DurationIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Admin Actions"
            value={850}
            unit=""
            previousValue={780}
            percentChange={9}
            icon={<AdminIcon />}
            color="info"
            isLoading={isLoading}
            magical
          />
        </Grid>
      </Grid>
      
      {/* Content tabs */}
      <Box sx={getTabsContainerStyles(theme)}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<UserIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<ActionIcon />} iconPosition="start" label="Actions" />
          <Tab icon={<UserIcon />} iconPosition="start" label="Users" />
          <Tab icon={<TimeIcon />} iconPosition="start" label="Activity Log" />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="User Activity Trends"
              subtitle="Actions and active users over time"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={userActivityTrends}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="date" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left" 
                    dataKey="totalActions" 
                    name="Total Actions" 
                    fill={theme.palette.primary.main} 
                    barSize={20} 
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="uniqueUsers" 
                    name="Unique Users" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={3} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Action Type Distribution"
              subtitle="Breakdown by action type"
              icon={<ActionIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={actionTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {actionTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="User Role Distribution"
              subtitle="Users by role"
              icon={<AdminIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={userRoleDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    labelLine={false}
                  >
                    <Cell fill={theme.palette.error.main} /> {/* Admin */}
                    <Cell fill={theme.palette.warning.main} /> {/* Manager */}
                    <Cell fill={theme.palette.success.main} /> {/* Creator */}
                    <Cell fill={theme.palette.info.main} /> {/* Viewer */}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value} users`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={8}>
            <ChartCard
              title="Session Activity by Hour"
              subtitle="User sessions throughout the day"
              icon={<TimeIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={userSessionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="hour" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sessions" 
                    name="Active Sessions" 
                    fill={alpha(theme.palette.primary.main, 0.2)} 
                    stroke={theme.palette.primary.main}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Actions Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Module Usage"
              subtitle="Actions by module"
              icon={<ActionIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={moduleUsageData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="module" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="views" name="Views" stackId="a" fill={theme.palette.primary.main} />
                  <Bar dataKey="creates" name="Creates" stackId="a" fill={theme.palette.success.main} />
                  <Bar dataKey="updates" name="Updates" stackId="a" fill={theme.palette.warning.main} />
                  <Bar dataKey="deletes" name="Deletes" stackId="a" fill={theme.palette.error.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Action Distribution Map"
              subtitle="Relationship between views and edits"
              icon={<ActionIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    type="number" 
                    dataKey="views" 
                    name="Views" 
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{ value: 'Views', position: 'insideBottom', fill: theme.palette.text.secondary, offset: -5 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="creates" 
                    name="Creates" 
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{ value: 'Creates', angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary }}
                  />
                  <ZAxis 
                    type="number" 
                    dataKey="updates" 
                    range={[60, 400]} 
                    name="Updates" 
                  />
                  <RechartsTooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Scatter 
                    name="Modules" 
                    data={moduleUsageData} 
                    fill={theme.palette.primary.main}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Action Types by Role"
              subtitle="User roles and their actions"
              icon={<ActionIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { role: 'Admin', view: 200, create: 150, update: 180, delete: 120 },
                    { role: 'Manager', view: 320, create: 85, update: 95, delete: 15 },
                    { role: 'Creator', view: 280, create: 220, update: 150, delete: 5 },
                    { role: 'Viewer', view: 450, create: 10, update: 25, delete: 0 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="role" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="view" name="View" stackId="a" fill={theme.palette.primary.main} />
                  <Bar dataKey="create" name="Create" stackId="a" fill={theme.palette.success.main} />
                  <Bar dataKey="update" name="Update" stackId="a" fill={theme.palette.warning.main} />
                  <Bar dataKey="delete" name="Delete" stackId="a" fill={theme.palette.error.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Users Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Top Active Users"
              subtitle="Most active users by action count"
              icon={<UserIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={topActiveUsers}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar 
                    yAxisId="left" 
                    dataKey="actions" 
                    name="Total Actions" 
                    fill={theme.palette.primary.main} 
                    barSize={30} 
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="avgDuration" 
                    name="Avg. Session (min)" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={3} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="User Activity Heatmap"
              subtitle="Most active times"
              icon={<TimeIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              <Box sx={{ 
                height: 350, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center', 
                p: 2 
              }}>
                <Typography variant="body1" mb={2}>Heatmap visualization would appear here</Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: 250, 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(5, 1fr)', 
                  gridTemplateRows: 'repeat(5, 1fr)', 
                  gap: 1 
                }}>
                  {Array.from({ length: 25 }).map((_, i) => {
                    const intensity = Math.random();
                    return (
                      <Box 
                        key={i}
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, intensity * 0.8),
                          borderRadius: 1
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="User Activity Table"
              subtitle="Detailed user activity metrics"
              icon={<UserIcon />}
              isLoading={isLoading}
              height={500}
              magical
            >
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Total Actions</TableCell>
                      <TableCell align="right">Views</TableCell>
                      <TableCell align="right">Creates</TableCell>
                      <TableCell align="right">Updates</TableCell>
                      <TableCell align="right">Deletes</TableCell>
                      <TableCell align="right">Avg Session (min)</TableCell>
                      <TableCell align="right">Last Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: 'John Smith', role: 'Admin', total: 520, views: 220, creates: 150, updates: 130, deletes: 20, avgSession: 45, lastActive: '2h ago' },
                      { name: 'Sarah Johnson', role: 'Manager', total: 480, views: 320, creates: 85, updates: 70, deletes: 5, avgSession: 38, lastActive: '5m ago' },
                      { name: 'Mike Williams', role: 'Creator', total: 350, views: 180, creates: 120, updates: 45, deletes: 5, avgSession: 42, lastActive: '3h ago' },
                      { name: 'Emily Brown', role: 'Manager', total: 320, views: 180, creates: 65, updates: 70, deletes: 5, avgSession: 36, lastActive: '1h ago' },
                      { name: 'David Lee', role: 'Creator', total: 280, views: 150, creates: 85, updates: 40, deletes: 5, avgSession: 40, lastActive: '20m ago' },
                      { name: 'Lisa Wang', role: 'Viewer', total: 180, views: 170, creates: 5, updates: 5, deletes: 0, avgSession: 25, lastActive: '6h ago' },
                      { name: 'Robert Chen', role: 'Creator', total: 250, views: 120, creates: 80, updates: 45, deletes: 5, avgSession: 35, lastActive: '30m ago' },
                      { name: 'Amanda Taylor', role: 'Viewer', total: 150, views: 145, creates: 0, updates: 5, deletes: 0, avgSession: 22, lastActive: '1d ago' },
                      { name: 'James Wilson', role: 'Admin', total: 480, views: 180, creates: 120, updates: 130, deletes: 50, avgSession: 50, lastActive: '10m ago' },
                      { name: 'Michelle Garcia', role: 'Manager', total: 350, views: 220, creates: 70, updates: 55, deletes: 5, avgSession: 38, lastActive: '1h ago' },
                    ].map((user, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={user.role} 
                            size="small"
                            color={
                              user.role === 'Admin' ? 'error' :
                              user.role === 'Manager' ? 'warning' :
                              user.role === 'Creator' ? 'success' : 'info'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{user.total}</TableCell>
                        <TableCell align="right">{user.views}</TableCell>
                        <TableCell align="right">{user.creates}</TableCell>
                        <TableCell align="right">{user.updates}</TableCell>
                        <TableCell align="right">{user.deletes}</TableCell>
                        <TableCell align="right">{user.avgSession}</TableCell>
                        <TableCell align="right">{user.lastActive}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Activity Log Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Recent Activity Log"
              subtitle="Latest user actions"
              icon={<ActionIcon />}
              isLoading={isLoading}
              height={600}
              magical
            >
              <TableContainer sx={{ maxHeight: 540 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Module</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userActivityLog.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>{log.user}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.action}
                            size="small"
                            icon={
                              log.action === 'Created' ? <CreateIcon fontSize="small" /> :
                              log.action === 'Updated' ? <EditIcon fontSize="small" /> :
                              log.action === 'Deleted' ? <DeleteIcon fontSize="small" /> :
                              log.action === 'Viewed' ? <ViewsIcon fontSize="small" /> :
                              <LoginIcon fontSize="small" />
                            }
                            color={
                              log.action === 'Created' ? 'success' :
                              log.action === 'Updated' ? 'warning' :
                              log.action === 'Deleted' ? 'error' : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{log.module}</TableCell>
                        <TableCell>{log.item}</TableCell>
                        <TableCell>{log.timestamp}</TableCell>
                        <TableCell>
                          <Button size="small" variant="text">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default UserActivityAnalytics;